import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { COMPANY, STATS, IMAGES } from "../../data/site";

function Counter({ to, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1500;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref} className="tabular-nums">
      {val}
      {suffix}
    </span>
  );
}

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-36 border-t border-black/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-px bg-[#FF331F]" />
              <span className="font-mono-ibm text-[11px] tracking-[0.2em] uppercase text-[#FF331F]">
                About / 01
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] font-medium">
              A senior consultancy.
              <br />
              No vanity layers.
            </h2>
          </div>

          <div className="lg:col-span-7 lg:pt-4">
            <p className="text-lg md:text-xl text-[#404040] leading-relaxed">
              {COMPANY.name} is an Ontario-based software solutions
              firm. We work alongside enterprise, healthcare and public-sector
              leaders to translate complex problems into systems that ship,
              scale and survive audits.
            </p>
            <p className="mt-6 text-base md:text-lg text-[#404040] leading-relaxed">
              Our team blends business systems analysis with hands-on
              engineering — closing the gap between strategy decks and
              production software. Every engagement is led by senior practitioners.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6 text-sm">
              <div className="border-t border-black/15 pt-4">
                <div className="font-mono-ibm text-xs text-[#404040] uppercase tracking-widest">
                  Office
                </div>
                <div className="mt-2 font-display text-lg leading-snug">
                  {COMPANY.address.line1}
                  <br />
                  {COMPANY.address.line2} {COMPANY.address.postal}
                </div>
              </div>
              <div className="border-t border-black/15 pt-4">
                <div className="font-mono-ibm text-xs text-[#404040] uppercase tracking-widest">
                  Director
                </div>
                <div className="mt-2 font-display text-lg leading-snug">
                  {COMPANY.director}
                  <br />
                  <span className="text-[#404040]">{COMPANY.directorTitle}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-20 md:mt-28 border-t border-b border-black/10 grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`py-10 md:py-14 px-4 md:px-8 ${
                i !== 0 ? "md:border-l border-black/10" : ""
              } ${i % 2 === 1 ? "border-l border-black/10 md:border-l" : ""}`}
            >
              <div
                className="font-display text-5xl md:text-6xl tracking-tighter font-medium"
                style={{ color: s.color }}
              >
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-sm text-[#404040]">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Office image */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 border border-black/10 overflow-hidden">
            <img
              src={IMAGES.about}
              alt="Office interior"
              className="w-full h-[320px] md:h-[460px] object-cover"
            />
          </div>
          <div className="lg:col-span-4">
            <div className="font-mono-ibm text-xs uppercase tracking-widest text-[#FF331F]">
              [ Our practice ]
            </div>
            <p className="mt-4 font-display text-2xl md:text-3xl leading-tight tracking-tight">
              Engineered locally in Ottawa, Ontario. Delivered across North America.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
